import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CampaignSubscriber } from '@/shared/types/campaigns';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { CampaignSubscriberParams, fetchAllCampaignSubscribers } from './useCampaignSubscribers';

type ExportFormat = 'csv' | 'json';
type SenderTypeMap = Map<string, string | undefined>;

const flattenSubscriberForCsv = (subscriber: CampaignSubscriber, senderTypeMap: SenderTypeMap) => ({
  email: subscriber.email ?? '',
  phone: subscriber.phone ?? '',
  channels: [
    ...new Set(
      subscriber.sender_history?.map((s) => senderTypeMap.get(s.sender_id)).filter(Boolean)
    ),
  ].join(', '),
  last_sent_at: subscriber.last_sent_at ?? '',
  last_delivered_at: subscriber.last_delivered_at ?? '',
  last_read_at: subscriber.last_read_at ?? '',
  last_replied_at: subscriber.last_replied_at ?? '',
  last_failed_at: subscriber.last_failed_at ?? '',
  error_codes:
    subscriber.sender_history
      ?.map((s) => s.status_code)
      .filter(Boolean)
      .join(', ') ?? '',
});

interface UseExportCampaignLogsOptions {
  campaignId: string | undefined;
  campaignName: string | undefined;
  params: CampaignSubscriberParams;
  campaignSenders: Array<{ sender_id: string; integration_type?: string }>;
}

export const useExportCampaignLogs = ({
  campaignId,
  campaignName,
  params,
  campaignSenders,
}: UseExportCampaignLogsOptions) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const exportLogs = useCallback(
    async (format: ExportFormat) => {
      if (!campaignId) {
        return;
      }

      setIsExporting(true);
      try {
        const subscribers = await fetchAllCampaignSubscribers(campaignId, params);

        if (subscribers.length === 0) {
          toast.info(t('campaigns.export_no_data'));
          return;
        }

        const safeName = (campaignName ?? campaignId).replace(/[^a-zA-Z0-9_-]/g, '_');
        const filename = `campaign_logs_${safeName}`;
        const senderTypeMap: SenderTypeMap = new Map(
          campaignSenders.map((s) => [s.sender_id, s.integration_type])
        );

        if (format === 'csv') {
          downloadCsv(
            subscribers.map((s) => flattenSubscriberForCsv(s, senderTypeMap)),
            filename
          );
        } else {
          downloadJson(subscribers, filename);
        }
      } catch {
        toast.error(t('campaigns.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [campaignId, campaignName, campaignSenders, params, t]
  );

  return { exportLogs, isExporting };
};
